import React from 'react';
import { motion } from 'framer-motion';
import { FaEdit, FaCheckCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { useSizeIssueReport } from '../../hooks/useSizeIssueReport';

const SizeIssueStatus = ({ order, onReportIssue }) => {
  const { sizeIssueReport, loading } = useSizeIssueReport(order.id, order.orderNumber);

  // Don't show anything if order is not delivered
  const shouldShow = order.status === 'Delivered' || order.status === 'delivered';
  if (!shouldShow) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="size-issue-section">
        <div className="size-issue-content">
          <div className="size-issue-text">
            <h4>Checking size issue status...</h4>
          </div>
        </div>
      </div>
    );
  }

  // If no report exists, show the report button
  if (!sizeIssueReport) {
    return (
      <div className="size-issue-section">
        <div className="size-issue-content">
          <div className="size-issue-text">
            <h4>Size not quite right?</h4>
            <p>We're here to help with any sizing concerns</p>
          </div>
          <motion.button 
            className="luxury-btn-size-issue-elegant"
            onClick={(e) => {
              e.stopPropagation();
              onReportIssue(order);
            }}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaEdit />
            Report Issue
          </motion.button>
        </div>
      </div>
    );
  }

  // If report exists, show status
  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return {
          icon: <FaClock />,
          text: 'Size Issue Reported',
          subtext: 'We\'re reviewing your report and will contact you soon',
          className: 'size-issue-reported-pending'
        };
      case 'in_progress':
        return {
          icon: <FaExclamationTriangle />,
          text: 'Size Issue In Progress',
          subtext: 'We\'re working on resolving your size issue',
          className: 'size-issue-reported-progress'
        };
      case 'resolved':
        return {
          icon: <FaCheckCircle />,
          text: 'Size Issue Resolved',
          subtext: 'Your size issue has been resolved',
          className: 'size-issue-reported-resolved'
        };
      default:
        return {
          icon: <FaClock />,
          text: 'Size Issue Reported',
          subtext: 'We\'re reviewing your report',
          className: 'size-issue-reported-pending'
        };
    }
  };

  const statusInfo = getStatusInfo(sizeIssueReport.status);

  return (
    <div className="size-issue-section">
      <div className={`size-issue-content ${statusInfo.className}`}>
        <div className="size-issue-text">
          <div className="size-issue-status-header">
            {statusInfo.icon}
            <h4>{statusInfo.text}</h4>
          </div>
          <p>{statusInfo.subtext}</p>
          {sizeIssueReport.issueType && (
            <div className="size-issue-details">
              <small>Issue: {sizeIssueReport.issueType}</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SizeIssueStatus;
